declare global {
  interface Window {
    flutter_inappwebview: {
      callHandler: (handlerName: string, ...args: any[]) => Promise<any>;
    };
  }
}

const sendFlutterCommand = async (command: string, args: any): Promise<any> => {
  if (!window.flutter_inappwebview) return null;
  return await window.flutter_inappwebview.callHandler(command, args);
};

/** True when running inside the Flutter native shell (bridge present). */
export const isFlutterBridgeAvailable = (): boolean => {
  return typeof window !== "undefined" && !!window.flutter_inappwebview;
};

export type BiometricCapability = {
  supported: boolean; // device has usable biometric hardware + enrolled
  platform: "ios" | "android" | ""; // reported by the native shell
  deviceId: string; // stable per-install device identifier
  biometricType?: string; // e.g. "face" | "fingerprint" | "strong"
};

/**
 * Human-friendly label for the device's biometric method, derived from what the
 * native shell reports. The server has a single generic "Biometric" provider;
 * the label is purely a presentation concern decided here.
 */
export const getBiometricLabel = (cap: BiometricCapability): string => {
  const type = (cap.biometricType || "").toLowerCase();
  if (cap.platform === "ios") {
    if (type === "fingerprint" || type === "touch") return "Touch ID";
    return "Face ID"; // iOS default / faceID
  }
  if (cap.platform === "android") {
    if (type === "face") return "Face Unlock";
    return "Fingerprint";
  }
  return "Biometrics";
};

/**
 * Ask the native shell whether the device supports biometrics (Face ID /
 * fingerprint) and which platform it is. Returns a not-supported result when
 * the bridge is absent (desktop/browser), so callers can no-op gracefully.
 */
export const getBiometricCapability = async (): Promise<BiometricCapability> => {
  const notSupported: BiometricCapability = { supported: false, platform: "", deviceId: "" };
  if (!isFlutterBridgeAvailable()) return notSupported;

  try {
    const res = await sendFlutterCommand("biometricCapability", {});
    if (!res) return notSupported;
    // The bridge may return a JSON string or an object depending on platform.
    const parsed = typeof res === "string" ? JSON.parse(res) : res;
    return {
      supported: Boolean(parsed.supported),
      platform: parsed.platform === "ios" || parsed.platform === "android" ? parsed.platform : "",
      deviceId: typeof parsed.deviceId === "string" ? parsed.deviceId : "",
      biometricType: typeof parsed.biometricType === "string" ? parsed.biometricType : undefined,
    };
  } catch {
    return notSupported;
  }
};

/** A single biometric authenticator class the device reports (diagnostics). */
export type BiometricDiagnosticCapability = {
  name: string; // e.g. "Face ID", "Touch ID", "Strong biometric", "Device PIN"
  class: string; // "strong" | "weak" | ""
  available: boolean;
  status: string; // human-readable, e.g. "available", "supported, none enrolled"
};

export type BiometricDiagnostics = {
  platform: "ios" | "android" | "";
  capabilities: BiometricDiagnosticCapability[];
};

/**
 * Hidden diagnostics: ask the native shell for the device's biometric
 * authenticator classes and their availability (strong/weak biometric, device
 * PIN). Returns an empty capability list when the bridge is absent.
 */
export const getBiometricDiagnostics = async (): Promise<BiometricDiagnostics> => {
  const empty: BiometricDiagnostics = { platform: "", capabilities: [] };
  if (!isFlutterBridgeAvailable()) return empty;

  try {
    const res = await sendFlutterCommand("biometricDiagnostics", {});
    if (!res) return empty;
    // The bridge may return a JSON string or an object depending on platform.
    const parsed = typeof res === "string" ? JSON.parse(res) : res;
    const rawCaps = Array.isArray(parsed?.capabilities) ? parsed.capabilities : [];
    const capabilities: BiometricDiagnosticCapability[] = rawCaps.map((c: any) => ({
      name: typeof c?.name === "string" ? c.name : "",
      class: typeof c?.class === "string" ? c.class : "",
      available: Boolean(c?.available),
      status: typeof c?.status === "string" ? c.status : "",
    }));
    return {
      platform: parsed?.platform === "ios" || parsed?.platform === "android" ? parsed.platform : "",
      capabilities,
    };
  } catch {
    return empty;
  }
};

export type BiometricEnrolResult = {
  success: boolean;
  publicKey: string; // base64 SPKI DER of the generated hardware key
  deviceId: string;
  platform: "ios" | "android" | "";
  message?: string;
};

/**
 * Ask the native shell to generate a hardware-backed keypair (Secure Enclave /
 * StrongBox), prompt the user for biometric consent, and return the PUBLIC key
 * for server registration. The private key never leaves the device.
 */
export const enrolBiometric = async (): Promise<BiometricEnrolResult> => {
  const failure: BiometricEnrolResult = { success: false, publicKey: "", deviceId: "", platform: "" };
  if (!isFlutterBridgeAvailable()) return failure;

  try {
    const res = await sendFlutterCommand("biometricEnrol", {});
    if (!res) return failure;
    const parsed = typeof res === "string" ? JSON.parse(res) : res;
    return {
      success: Boolean(parsed.success) && !!parsed.publicKey,
      publicKey: typeof parsed.publicKey === "string" ? parsed.publicKey : "",
      deviceId: typeof parsed.deviceId === "string" ? parsed.deviceId : "",
      platform: parsed.platform === "ios" || parsed.platform === "android" ? parsed.platform : "",
      message: typeof parsed.message === "string" ? parsed.message : undefined,
    };
  } catch (e: any) {
    return { ...failure, message: e?.message };
  }
};

export type BiometricSignResult = {
  success: boolean;
  signature: string; // base64 DER ECDSA signature over the challenge
  message?: string;
};

/**
 * Ask the native shell to sign the given server challenge with the device's
 * hardware key, prompting the user for biometric authentication. Returns the
 * base64 signature for the server to verify.
 */
export const signBiometricChallenge = async (challenge: string): Promise<BiometricSignResult> => {
  const failure: BiometricSignResult = { success: false, signature: "" };
  if (!isFlutterBridgeAvailable()) return failure;

  try {
    const res = await sendFlutterCommand("biometricSign", challenge);
    if (!res) return failure;
    const parsed = typeof res === "string" ? JSON.parse(res) : res;
    return {
      success: Boolean(parsed.success) && !!parsed.signature,
      signature: typeof parsed.signature === "string" ? parsed.signature : "",
      message: typeof parsed.message === "string" ? parsed.message : undefined,
    };
  } catch (e: any) {
    return { ...failure, message: e?.message };
  }
};
